import { useCallback, useEffect, useState } from "react";

import { getPublicCaptchaSiteKey } from "../config/public-runtime-config";

// Type definition for reCAPTCHA Enterprise
declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
    };
  }
}

interface UseReCaptchaOptions {
  action?: string;
}

/**
 * Custom hook to handle Google reCAPTCHA Enterprise v3 verification
 * @param options Configuration options for reCAPTCHA
 * @returns Object containing reCAPTCHA state and verification method
 */
export const useReCaptcha = (options: UseReCaptchaOptions = {}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get site key from environment variables
  const getSiteKey = useCallback(() => {
    // Try to get the key from either environment variable
    const siteKey = getPublicCaptchaSiteKey();

    if (!siteKey) {
      setError("reCAPTCHA site key is not defined");
      return "";
    }

    return siteKey;
  }, []);

  // Load reCAPTCHA script
  useEffect(() => {
    // Check if reCAPTCHA is already loaded
    if (window.grecaptcha && window.grecaptcha.enterprise) {
      setIsLoaded(true);
      return;
    }

    const siteKey = getSiteKey();
    if (!siteKey) {
      return; // Error is already set in getSiteKey
    }

    // Create script element
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    // Handle script loading
    script.onload = () => {
      setIsLoaded(true);
      setError(null);
    };

    script.onerror = () => {
      setError("Error loading reCAPTCHA script");
    };

    // Add script to document
    document.head.appendChild(script);

    return () => {
      // Clean up script when component unmounts
      try {
        document.head.removeChild(script);
      } catch (e) {
        // Script might already be removed
        console.error(e);
      }
    };
  }, [getSiteKey]);

  // Execute reCAPTCHA verification
  const executeReCaptcha = useCallback(
    async (actionOverride?: string) => {
      const siteKey = getSiteKey();
      if (!siteKey) {
        return ""; // Error is already set in getSiteKey
      }

      if (!isLoaded || !window.grecaptcha || !window.grecaptcha.enterprise) {
        setError("reCAPTCHA is not loaded");
        return "";
      }

      try {
        // Use the override action if provided, otherwise fall back to options action or default
        const action = actionOverride || options.action || "submit";

        // Execute reCAPTCHA with the site key
        const token = await window.grecaptcha.enterprise.execute(siteKey, {
          action,
        });

        setError(null);
        return token;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "reCAPTCHA verification failed";
        setError(errorMessage);
        return "";
      }
    },
    [isLoaded, getSiteKey, options.action]
  );

  return {
    isLoaded,
    error,
    executeReCaptcha,
  };
};
